<template>
  <v-card
    id="screenContent"
    class="vetalfa-view"
    :max-width="_getMaxWidth()"
    flat
  >
    <div class="vetalfa-global-header" ref="screenHeader">
      <vetalfa-small-header
        showBreadCrumbAndTitle
        :title="_getLabel('PRESCRIPTIONS')"
        :breadcrumb="_getLabel('PRESCRIPTIONS')"
      ></vetalfa-small-header>
      <v-row no-gutters justify="center">
        <v-row no-gutters align="center" class="vetalfa-max-width">
          <v-col cols="12" class="px-4 my-2" v-if="hasCompleteRegistration">
            <patients />
          </v-col>
          <v-col cols="12" class="px-4 ma-0" v-if="prescriptionPages.length">
            <v-tabs
              active-class="primary--text font-weight-bold"
              class="ma-0 pa-0"
              slider-color="primary"
              v-model="pageModel"
              show-arrows
              :next-icon="mdiChevronRight"
              :prev-icon="mdiChevronLeft"
            >
              <v-tab
                class="text-none"
                v-for="(_page, index) in prescriptionPages"
                :key="`page${index}`"
                >{{ `${_getLabel("TAB_GUIDE")} ${parseInt(index) + 1}` }}</v-tab
              >
            </v-tabs>
          </v-col>
        </v-row>
      </v-row>
      <v-row no-gutters justify="center" v-if="prescriptionPages.length">
        <v-divider class="vetalfa-max-width"></v-divider>
      </v-row>
    </div>
    <div ref="screenContent">
      <v-row
        v-if="!prescriptionPages.length && hasCompleteRegistration"
        no-gutters
        class="ma-0 pa-0"
        justify="center"
      >
        <v-col cols="12" class="px-4 my-2">
          <v-alert
            @click="routeRedirect({ name: 'accounts' })"
            :icon="mdiAlertCircleOutline"
            outlined
            color="grey darken-1"
            class="vetalfa-pointer vetalfa-card-grey-border"
          >
            <p
              class="subtitle-2 grey--text text--darken-1 ma-0 pa-0 mb-1"
              v-html="_getLabel('CLICK_TO_EDIT_YOUR_MEDICAL_REGISTRATION')"
            ></p>
          </v-alert>
        </v-col>
        <v-btn
          @click="startPrescription"
          rounded
          color="primary"
          :disabled="!patient"
          class="text-none px-10 mt-3 subtitle-1"
          large
          >{{ _getLabel("START_PRESCRIPTION") }}
        </v-btn>
      </v-row>
      <search
        :items="filteredListOfDrugs"
        @fetch-data="filterDrugsFromSearch"
        :loadingTrigger="searchLoadingTrigger"
        :searchTriggerModel="searchTriggerModel"
        :filters="searchFilters"
        :filterValue="0"
        :noDataPlaceholder="_getLabel('DRUGS_NO_DATA')"
        :title="_getLabel('PRESCRIPTIONS_SEARCH')"
        ads="prescriptions"
      >
        <template v-slot:placeholder>
          <v-row no-gutters justify="center" class="px-3">
            <v-expansion-panels class="vetalfa-card-grey-border" flat>
              <v-expansion-panel active-class="vetalfa-card-primary-border">
                <v-expansion-panel-header class="know-more-ai-search">
                  <v-row no-gutters align="center">
                    <span class="pr-2 subtitle-2">
                      Dicas para otimizar a sua busca
                    </span>
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pb-5">
                  <vetalfa-section-item
                    v-for="searchTip in searchTips"
                    :key="searchTip"
                    :prop="searchTip"
                  ></vetalfa-section-item>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-row>
        </template>
        <template v-slot:appendItem>
          <v-btn
            @click="addFreeTextDrug"
            v-if="!selectedDrugFromSearch"
            rounded
            color="primary"
            class="text-none ma-0 pa-0 px-10 mb-4"
          >
            <v-icon small>{{ mdiPlus }}</v-icon>
            {{ _getLabel("ADD_FREE_TEXT") }}
          </v-btn>
        </template>
        <template v-slot:item="{ item }">
          <v-card
            v-if="item.name || item.drug"
            flat
            @click="startDrugSelectionFromSearch(item)"
            width="100%"
            class="px-2 py-3 mt-2 vetalfa-card-grey-border vetalfa-pointer"
          >
            <v-icon
              x-small
              style="position: absolute; top: 42%; right: 8px; translateY(-50%)"
            >
              {{ mdiChevronRight }}
            </v-icon>
            <v-row class="ma-0 pa-0 vetalfa-row" align="start">
              <v-col :cols="12" class="ma-0 pa-0">
                <v-row class="ma-0 pa-0" align="start">
                  <p
                    class="ma-0 pa-0 black--text caption"
                    v-html="getSearchItemName(item) || item.drug"
                  ></p>
                </v-row>
                <v-row class="ma-0 pa-0 black--text">
                  <v-chip
                    x-small
                    color="secondary"
                    class="mr-1 mt-2 primary--text"
                  >
                    {{
                      _getLabel(
                        { 0: "COMPOSITION", 1: "VETERINARY", 2: "HUMAN" }[
                          item.type
                        ] || "ACTIVE_PRINCIPLE_SINGULAR"
                      )
                    }}
                  </v-chip>
                  <v-chip
                    x-small
                    color="secondary"
                    v-if="item.brand"
                    class="mr-1 mt-2 primary--text"
                  >
                    {{ _capitalizeAllFirstLetters(item.brand) }}
                  </v-chip>
                </v-row>
                <v-row v-if="item.name && item.drug" class="ma-0 pa-0 mt-5">
                  <p
                    class="caption text-left ma-0 pa-0 grey--text text--darken-1 font-italic"
                    style="line-height: 15px"
                  >
                    {{ item.drug }}
                  </p>
                </v-row>
              </v-col>
            </v-row>
          </v-card>
        </template>
      </search>
      <draggable
        v-model="prescriptionPages[pageModel]"
        handle=".drag-handle"
        class="mt-1 mb-2 mx-2"
      >
        <transition-group>
          <v-row
            v-for="{
              id,
              name,
              option,
              posology,
              additionalInformation,
              quantity,
              freeText,
              route,
            } in prescriptionPages[pageModel]"
            justify="space-between"
            aling="center"
            class="mb-3 pa-0 ma-0"
            :key="`prescriptionIndex-${id}`"
          >
            <v-col cols="12" class="ma-0 pa-0">
              <v-row class="vetalfa-row ma-0 pa-0" align="center">
                <v-icon
                  class="mr-2 drag-handle vetalfa-pointer"
                  color="primary"
                  size="20"
                >
                  {{ _icon("drag") }}
                </v-icon>
                <v-card
                  class="ma-0 pa-2 vetalfa-card-grey-border vetalfa-pointer"
                  flat
                  width="90%"
                >
                  <v-row no-gutters class="black--text subtitle-2">
                    <span>{{
                      getDrugTextDisplay(id, name, freeText ? null : option)
                    }}</span>
                  </v-row>
                  <v-row no-gutters class="caption grey--text text--darken-2">
                    {{ posology }}
                  </v-row>
                  <v-row
                    v-if="additionalInformation"
                    no-gutters
                    class="caption grey--text text--darken-2"
                  >
                    {{ additionalInformation }}
                  </v-row>
                  <v-row
                    no-gutters
                    v-if="route"
                    class="caption grey--text text--darken-2"
                  >
                    {{ `Via de Administração: ${route}` }}
                  </v-row>
                  <v-row
                    no-gutters
                    v-if="quantity"
                    class="caption grey--text text--darken-2"
                  >
                    {{ `Quantidade: ${quantity}` }}
                  </v-row>
                  <v-row
                    align="center"
                    justify="end"
                    class="ma-0 pa-0 mt-2 vetalfa-row"
                  >
                    <v-btn
                      @click="editItemFromPrescription(id)"
                      rounded
                      icon
                      class="mx-1 secondary"
                    >
                      <v-icon small color="primary">{{
                        mdiPencilOutline
                      }}</v-icon>
                    </v-btn>
                    <v-btn
                      @click="removeItemFromPrescription(id)"
                      rounded
                      icon
                      class="mx-1 secondary"
                    >
                      <v-icon small color="primary">{{
                        mdiTrashCanOutline
                      }}</v-icon>
                    </v-btn>
                  </v-row>
                </v-card>
              </v-row>
            </v-col>
            <v-col cols="3" class="ma-0 pa-0 pl-2"> </v-col>
          </v-row>
        </transition-group>
      </draggable>
      <algorithms :headerHeightOffset="50" v-if="!hasCompleteRegistration">
      </algorithms>
      <vetalfa-dialog
        v-on:dialogClose="drugsDialogModel = false"
        :bottom="_xsOnly()"
        :toggleModel="drugsDialogModel"
        :fullscreen="false"
        max-width="700px"
        :height="dialogHeight"
        :smallHeader="false"
        :featureMode="false"
        background-color="secondary"
        :dialogHeader="{
          title: _getLabel('ADD_ITEM'),
        }"
      >
        <v-row v-if="selectedDrugFromSearch" class="my-2 px-4 pt-2">
          <v-col cols="12" class="ma-0 pa-0"> </v-col>
          <algorithms
            :headerHeightOffset="50"
            :customNodesNumber="selectedDrugFromSearch.freeText ? 0 : 4"
            :triggerFormatTimelineHeight="triggerFormatTimelineHeight"
            :dialogContainer="true"
          >
            <template
              v-slot:customNodes
              v-if="!selectedDrugFromSearch.freeText"
            >
              <v-timeline-item
                id="algorithm-custom-1"
                :icon="null"
                color="rgb(0, 0, 0, 0.14)"
                icon-color="primary"
                fill-dot
                class="ma-0 pa-0"
                small
              >
                <v-row class="ma-0 pa-0 mb-4" justify="start" align="center">
                  <v-col cols="12" class="ma-0 pa-0 pl-2 mb-2">
                    <p class="ma-0 pa-0 subtitle-2 primary--text">
                      {{ _getLabel("CHOSEN_PA") }}
                    </p>
                  </v-col>
                  <v-col
                    cols="12"
                    class="ma-0 pa-0 pr-2"
                    v-for="{
                      drug,
                      category,
                      drugCategory,
                      drugId,
                    } in drugsList"
                    :key="`pa-${drug}`"
                  >
                    <vetalfa-reference-card
                      v-if="drugId"
                      class="ma-0 pa-0"
                      :reference="{
                        type: 1,
                        category: 'Medicamentos',
                        path: `/categories/${
                          drugCategory || category
                        }/drugs/${drugId}`,
                        name: drug,
                      }"
                    ></vetalfa-reference-card>
                    <vetalfa-section-item
                      v-else
                      :prop="drug"
                    ></vetalfa-section-item>
                  </v-col>
                </v-row>
              </v-timeline-item>
              <v-timeline-item
                id="algorithm-custom-2"
                :icon="null"
                color="rgb(0, 0, 0, 0.14)"
                icon-color="primary"
                fill-dot
                class="ma-0 pa-0"
                small
              >
                <v-row class="ma-0 pa-0 mb-2" justify="start" align="center">
                  <v-col cols="12" class="ml-2 ma-0 pa-0">
                    <p class="ma-0 pa-0 subtitle-2 primary--text">
                      {{ _getLabel("PRESCRIPTIONS_WHICH_DOSE_QUESTION") }}
                    </p>
                  </v-col>
                  <v-col cols="12" class="ma-0 pa-0 pr-5 mt-2">
                    <v-menu
                      bottom
                      rounded
                      :offset-y="true"
                      allow-overflow
                      v-if="clickableDrugs === 1"
                      max-width="600px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-row no-gutters v-bind="attrs" v-on="on">
                          <v-col cols="12">
                            <v-card
                              v-if="!selectedDrugFromSearch.dose"
                              flat
                              class="vetalfa-card-grey-border vetalfa-pointer"
                              style="position: relative"
                              width="100%"
                            >
                              <v-row
                                no-gutters
                                class="vetalfa-row pa-2"
                                align="center"
                                justify="center"
                              >
                                <span>
                                  {{ _getLabel("PRESCRIPTIONS_SELECT_DOSE") }}
                                </span>
                              </v-row>
                              <v-icon
                                x-small
                                style="position: absolute; top: 40%; right: 8px; translateY(-50%)"
                                >{{ mdiChevronDown }}</v-icon
                              >
                            </v-card>
                            <vetalfa-dose-card
                              v-else
                              width="100%"
                              isCard
                              :prop="selectedDrugFromSearch.dose"
                              class="mr-n2"
                              :clickStop="true"
                            ></vetalfa-dose-card>
                            <v-row
                              no-gutters
                              v-if="selectedDrugFromSearch.dose"
                              justify="end"
                              class="pa-2 mt-1"
                            >
                              <v-btn
                                small
                                @click="
                                  routePathRedirect({
                                    path: `/categories/${
                                      drugsList[0].drugCategory ||
                                      drugsList[0].category
                                    }/drugs/${drugsList[0].drugId}/doses/${
                                      selectedDrugFromSearch.dose.id
                                    }`,
                                  })
                                "
                                text
                                class="primary--text text-none ma-0 pa-0 text-decoration-underline"
                              >
                                {{ _getLabel("SEE_COMPLETE_DOSE") }}
                              </v-btn>
                            </v-row>
                          </v-col>
                        </v-row>
                      </template>
                      <v-row
                        class="ma-0 secondary px-0 py-2"
                        style="max-height: 300px; overflow-y: auto"
                      >
                        <v-col
                          :key="index"
                          v-for="(dose, index) in getDosesForPatientSpecies(
                            selectedDrugFromSearch.doses
                          )"
                          cols="12"
                          class="ma-0 pa-0 py-2"
                          @click="selectDoseForNewItem(dose)"
                        >
                          <vetalfa-dose-card
                            :key="`${index}-${dose.title}`"
                            width="100%"
                            isCard
                            :prop="dose"
                            :index="index"
                            :clickStop="true"
                          ></vetalfa-dose-card>
                        </v-col>
                      </v-row>
                    </v-menu>
                    <vetalfa-section-item
                      v-if="clickableDrugs > 1"
                      prop="Clique nos princípios ativos acima para visualizar as doses disponíveis para cada um deles."
                    >
                    </vetalfa-section-item>
                    <vetalfa-section-item
                      v-if="clickableDrugs === 0"
                      prop="Nenhuma dose disponível para os princípios ativos da sua seleção."
                    >
                    </vetalfa-section-item>
                    <vetalfa-section-item
                      prop="Se você tiver alguma dúvida sobre doses, experimente perguntar à nossa inteligência artificial: "
                    >
                      <span
                        @click="routePathRedirect({ path: '/ai-conversations/new' })"
                        class="text-decoration-underline primary--text vetalfa-pointer"
                        >clique aqui.</span
                      >
                    </vetalfa-section-item>
                  </v-col>
                </v-row>
              </v-timeline-item>
              <v-timeline-item
                id="algorithm-custom-3"
                :icon="null"
                color="rgb(0, 0, 0, 0.14)"
                icon-color="primary"
                fill-dot
                class="ma-0 pa-0"
                small
              >
                <v-row
                  class="pl-2 ma-0 pa-0 mb-3"
                  justify="start"
                  align="center"
                >
                  <v-col cols="12" class="ma-0 pa-0">
                    <p class="ma-0 pa-0 subtitle-2 primary--text">
                      {{ _getLabel("PRESCRIPTIONS_WHICH_DISPLAY_QUESTION") }}
                    </p>
                  </v-col>
                  <v-col
                    cols="12"
                    class="ma-0 pa-0 pr-5 mt-2"
                    @click="prescriptionItemSelectionClick"
                  >
                    <v-card
                      v-if="!selectedDrugFromSearch.option"
                      flat
                      class="vetalfa-card-grey-border vetalfa-pointer"
                      style="position: relative"
                      width="100%"
                    >
                      <v-row
                        no-gutters
                        class="vetalfa-row pa-2"
                        align="center"
                        justify="center"
                      >
                        <span>
                          {{ _getLabel("PRESCRIPTIONS_SELECT_DISPLAY") }}
                        </span>
                      </v-row>
                      <v-icon
                        x-small
                        style="position: absolute; top: 40%; right: 8px; translateY(-50%)"
                        >{{ mdiChevronDown }}</v-icon
                      >
                    </v-card>
                    <v-card
                      v-else
                      flat
                      width="100%"
                      class="px-2 py-2 mt-2 vetalfa-card-grey-border vetalfa-pointer"
                    >
                      <v-icon
                        x-small
                        style="position: absolute; top: 42%; right: 8px; translateY(-50%)"
                      >
                        {{ mdiChevronRight }}
                      </v-icon>
                      <v-row class="ma-0 pa-0 vetalfa-row" align="start">
                        <v-col :cols="12" class="ma-0 pa-0">
                          <v-row class="ma-0 pa-0" align="start">
                            <p
                              class="ma-0 pa-0 black--text caption pr-2"
                              v-html="getSearchItemName(selectedDrugFromSearch)"
                            ></p>
                          </v-row>
                          <v-row class="ma-0 pa-0 black--text">
                            <v-chip
                              x-small
                              color="secondary"
                              class="mr-1 mt-2 primary--text"
                            >
                              {{
                                _getLabel(
                                  {
                                    0: "COMPOSITION",
                                    1: "VETERINARY",
                                    2: "HUMAN",
                                  }[selectedDrugFromSearch.type] ||
                                    "ACTIVE_PRINCIPLE_SINGULAR"
                                )
                              }}
                            </v-chip>
                            <v-chip
                              x-small
                              color="secondary"
                              v-if="selectedDrugFromSearch.brand"
                              class="mr-1 mt-2 primary--text"
                            >
                              {{
                                _capitalizeAllFirstLetters(
                                  selectedDrugFromSearch.brand
                                )
                              }}
                            </v-chip>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-card>

                    <v-row
                      no-gutters
                      v-if="
                        selectedDrugFromSearch.option &&
                        [1, 2].includes(selectedDrugFromSearch.type)
                      "
                      justify="end"
                      class="pa-2 mt-1"
                    >
                      <v-btn
                        small
                        @click="
                          openProductInformationAction(selectedDrugFromSearch)
                        "
                        text
                        class="primary--text text-none ma-0 pa-0 text-decoration-underline"
                      >
                        {{
                          _getLabel(
                            selectedDrugFromSearch.type === 1
                              ? "SEE_VETERINARY_PRESENTATION"
                              : "SEE_HUMAN_PRESENTATION"
                          )
                        }}
                      </v-btn>
                    </v-row>
                  </v-col>
                </v-row>
              </v-timeline-item>
              <v-timeline-item
                id="algorithm-custom-4"
                :icon="null"
                color="rgb(0, 0, 0, 0.14)"
                icon-color="primary"
                fill-dot
                class="ma-0 pa-0"
                small
              >
                <v-row
                  class="pl-2 ma-0 pa-0 mb-2"
                  justify="start"
                  align="center"
                >
                  <v-col cols="12" class="ma-0 pa-0">
                    <p class="ma-0 pa-0 subtitle-2 primary--text">
                      {{ _getLabel("DOSE_CALCULATOR") }}
                    </p>
                  </v-col>

                  <v-col cols="12" class="ma-0 pa-0 pr-5 mt-2">
                    <v-card
                      v-if="!expandCalculator"
                      flat
                      class="vetalfa-card-grey-border vetalfa-pointer"
                      style="position: relative"
                      width="100%"
                      @click="expandCalculatorClick"
                    >
                      <v-row
                        no-gutters
                        class="vetalfa-row pa-2"
                        align="center"
                        justify="center"
                      >
                        <span>
                          {{ _getLabel("EXPAND_CALCULATOR") }}
                        </span>
                      </v-row>
                      <v-icon
                        x-small
                        style="position: absolute; top: 40%; right: 8px; translateY(-50%)"
                        >{{ mdiChevronDown }}</v-icon
                      >
                    </v-card>
                    <dose-calculator
                      componentMode
                      v-else
                      :modified="true"
                      class="vetalfa-card-grey-border vetalfa-pointer"
                      @response="triggerFormatTimelineHeight = _getTimestamp()"
                    ></dose-calculator>
                  </v-col>
                </v-row>
              </v-timeline-item>
            </template>
          </algorithms>
          <v-row no-gutters style="height: 100px"></v-row>
        </v-row>
      </vetalfa-dialog>
      <v-row
        no-gutters
        justify="space-around"
        v-if="prescriptionPages.length"
        class="vetalfa-button-bottom-combo white"
      >
        <v-col
          :cols="_xsOnly() ? 4 : 3"
          v-for="action in prescriptionActions"
          :key="action.name"
          class="vetalfa-pointer"
        >
          <v-row justify="center" no-gutters>
            <v-btn
              :fab="_isMobile()"
              :rounded="!_isMobile()"
              :small="_isMobile()"
              color="primary"
              outlined
              class="text-none"
              :class="{ 'px-6': !_isMobile() }"
              @click="action.method"
            >
              <v-icon
                size="20"
                color="primary"
                :class="{ 'mr-2': !_isMobile() }"
                >{{ action.icon }}</v-icon
              >
              <span v-if="!_isMobile()">{{ action.label }}</span>
            </v-btn>
          </v-row>
          <v-row
            @click="action.method"
            justify="center"
            no-gutters
            class="mt-3"
            v-if="_isMobile()"
          >
            <p class="ma-0 pa-0 text-center primary--text">
              {{ action.label }}
            </p>
          </v-row>
        </v-col>
      </v-row>
      <v-row
        v-if="prescriptionPages.length || !hasCompleteRegistration"
        no-gutters
        style="height: 100px"
      ></v-row>
    </div>
  </v-card>
</template>
<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import Search from "@/components/Search.vue";
import Algorithms from "@/algorithms/Algorithms.vue";
import Patients from "@/components/Patients.vue";
import DoseCalculator from "@/views/DoseCalculator.vue";
import draggable from "vuedraggable";
import {
  mdiPencilOutline,
  mdiTrashCanOutline,
  mdiPlus,
  mdiMagnifyPlusOutline,
  mdiFileSendOutline,
  mdiChevronDown,
  mdiChevronRight,
  mdiChevronLeft,
  mdiChevronUp,
  mdiAlertCircleOutline,
  mdiFileDocumentRemoveOutline,
} from "@mdi/js";
export default {
  name: "Prescriptions",
  props: {},

  components: {
    algorithms: Algorithms,
    search: Search,
    draggable,
    patients: Patients,
    "dose-calculator": DoseCalculator,
  },

  data() {
    return {
      expandCalculator: false,
      dialogHeight: null,
      mdiPencilOutline,
      mdiTrashCanOutline,
      mdiPlus,
      mdiMagnifyPlusOutline,
      mdiFileSendOutline,
      mdiChevronDown,
      mdiChevronRight,
      mdiChevronUp,
      mdiChevronLeft,
      mdiAlertCircleOutline,
      mdiFileDocumentRemoveOutline,
      triggerFormatTimelineHeight: false,
      searchTriggerModel: false,
      filteredListOfDrugs: [],
      pageModel: 0,
      searchLoadingTrigger: null,
      drugsDialogModel: false,
      hasCompleteRegistration: true,
      searchTips: [
        "Digite o nome do princípio ativo, medicamento veterinário ou humano que você deseja prescrever.",
        'Para buscar por Composições (apresentações comerciais humanas sem uma marca específica), selecione a aba "Composições".',
        'Para buscar por Medicamentos Veterinários selecione a aba "Veterinários".',
        'Para buscar por Medicamentos Humanos originais, similares, genéricos e afins selecione a aba "Humanos".',
        'Caso queira adicionar o agente manualmente, clique abaixo em "+ Adicionar manualmente".',
      ],
    };
  },

  computed: {
    ...mapGetters("algorithms", ["flowEndTrigger", "getModels"]),
    ...mapGetters("prescriptions", [
      "prescription",
      "selectedDrug",
      "edition",
      "shareDate",
    ]),
    ...mapGetters("patients", ["patient"]),
    ...mapGetters("general", ["user"]),
    drugsList() {
      let { drugsList } = this.selectedDrugFromSearch || {};
      if (!drugsList && this.selectedDrugFromSearch.option) return [];
      else if (!this.selectedDrugFromSearch.option) {
        return [{ ...this.selectedDrugFromSearch }];
      } else if (typeof drugsList === "string") {
        return JSON.parse(drugsList);
      }
      return drugsList;
    },
    clickableDrugs() {
      return this.drugsList.filter((d) => !!d.drugId).length;
    },
    searchFilters() {
      return [
        this._getLabel("COMPOSITIONS"),
        this._getLabel("VETERINARIANS"),
        this._getLabel("HUMANS"),
      ];
    },
    prescriptionActions() {
      return [
        {
          name: "deleteDraft",
          icon: mdiFileDocumentRemoveOutline,
          label: this._getLabel("DELETE_DRAFT"),
          method: async () => {
            await this.deletePrescriptionDraftAction({
              callback: () => {
                this._setupScreenSizes(this);
              },
            });
          },
        },
        {
          name: "addNewItem",
          icon: mdiMagnifyPlusOutline,
          label: this._getLabel("ADD_ITEM"),
          method: this.prescriptionItemSelectionClick,
        },
        {
          name: "viewAndContinue",
          icon: mdiFileSendOutline,
          label: this._getLabel("REVIEW_SEND"),
          method: this.goToPrescriptionsSummaryAction,
        },
      ];
    },
    prescriptionPages: {
      get() {
        return this.prescription;
      },
      set(value) {
        this.setPrescription(value);
      },
    },
    selectedDrugFromSearch: {
      get() {
        return this.selectedDrug;
      },
      set(value) {
        this.setSelectedDrug(value);
      },
    },
    editionMode: {
      get() {
        return this.edition;
      },
      set(value) {
        this.setEdition(value);
      },
    },
  },

  watch: {
    patient() {
      this._setupScreenSizes(this);
    },
    async flowEndTrigger() {
      if (this.drugsDialogModel) {
        this.searchTriggerModel = false;
        this.$nextTick(() => {
          if (this.editionMode) {
            this.saveDrugEditionToPrescriptionList();
          } else {
            this.addDrugToPrescriptionList();
          }
          this.drugsDialogModel = false;
        });
      } else if (!this.hasCompleteRegistration) {
        this.completeUserRegistration();
      }
    },
    drugsDialogModel(val) {
      if (!val) {
        this.selectedDrugFromSearch = null;
        this.expandCalculator = false;
      }
    },

    getModels: {
      handler(val) {
        this.setSelectedDrugFromSearchWithModels(val);
      },
      deep: true,
    },

    prescriptionPages: {
      handler() {
        this._setupScreenSizes(this);
      },
      deep: true,
    },
  },

  methods: {
    ...mapMutations("general", ["setConfirmModel"]),
    ...mapMutations("prescriptions", [
      "setPrescription",
      "setSelectedDrug",
      "setEdition",
    ]),
    ...mapActions("general", [
      "saveUserData",
      "routeRedirect",
      "routePathRedirect",
    ]),
    ...mapActions("algorithms", ["clearNodesModelsAndInputs"]),
    ...mapActions("prescriptions", [
      "continueDrugSelectionAfterDisplayChoiceAction",
      "addFreeTextDrugAction",
      "filterDrugsFromSearchAction",
      "getItemDosesAndOptionsAction",
      "startPrescriptionClickAction",
      "deletePrescriptionDraftAction",
      "goToPrescriptionsSummaryAction",
      "updateDraftFromPrescriptionAction",
      "clearPrescriptionAction",
      "checkForMedicalData",
      "openProductInformationAction",
    ]),
    ...mapActions("patients", ["hasValidPatientSelected"]),
    getDosesForPatientSpecies(doses) {
      let filteredDoses = doses.filter((dose) => {
        return dose.animals.includes(this.patient.species);
      });
      return filteredDoses.length ? filteredDoses : doses;
    },
    expandCalculatorClick() {
      this.expandCalculator = !this.expandCalculator;
      this.$nextTick(() => {
        setTimeout(() => {
          this.triggerFormatTimelineHeight = this._getTimestamp();
        }, 100);
      });
    },
    async startPrescription() {
      const { hasCompleteRegistration, hasPatient } =
        await this.startPrescriptionClickAction();
      this.hasCompleteRegistration = hasCompleteRegistration;
      if (hasCompleteRegistration && hasPatient) {
        this.prescriptionItemSelectionClick();
      }
      this._setupScreenSizes(this);
    },
    completeUserRegistration() {
      let user = {
        nationality: parseInt(process.env.VUE_APP_BRAZILIAN_NATIONALITY),
        questions: { ...this.user.questions },
      };
      user["questions"]["veterinaryStatus"] = parseInt(
        process.env.VUE_APP_VETERINARY_STATUS_PROFESSIONAL
      );
      this.saveUserData({
        user,
        successCallback: () => {
          this.hasCompleteRegistration = true;
          this._setupScreenSizes(this);
        },
      });
    },

    prescriptionItemSelectionClick() {
      this.searchTriggerModel = this._getTimestamp();
    },

    getSearchItemName(item) {
      const { name, option } = item;
      if (!name) {
        return null;
      }
      let text = name.trim();
      if (option) {
        if (option.concentration) {
          text += ` ${option.concentration.trim() || ""}`;
        }
        if (option.display) {
          text += `<span class="grey--text text--darken-2">, ${option.display.trim()}</span>`;
        }
        if (option.package) {
          text += `<span class="grey--text text--darken-2"> (${option.package.trim()})</span>`;
        }
      }
      return text;
    },

    selectDoseForNewItem(item) {
      this.selectedDrugFromSearch["dose"] = item;
      this.$nextTick(() => {
        this.triggerFormatTimelineHeight = this._getTimestamp();
      });
    },

    removeItemFromPrescription(id) {
      if (
        this.prescriptionPages.length === 1 &&
        this.prescriptionPages[0].length === 1
      ) {
        return this.deletePrescriptionDraftAction({
          callback: () => {
            this._setupScreenSizes(this);
          },
        });
      }
      this.setConfirmModel({
        text: this._getMessage("PRESCRIPTION_EXCLUSION_CONFIRMATION"),
        callback: () => {
          this.removeItemFromPrescriptionConfirmation(id);
        },
        confirmText: this._getLabel("YES"),
        closeText: this._getLabel("NO"),
      });
    },
    removeItemFromPrescriptionConfirmation(id) {
      for (let index in this.prescriptionPages) {
        this.prescriptionPages[index] = this.prescriptionPages[index].filter(
          (prescription) => prescription.id !== id
        );
      }

      this.checkAndAdjustEmptyTabs();
    },

    editItemFromPrescription(id) {
      for (let index in this.prescriptionPages) {
        const item = this.prescriptionPages[index].find(
          (prescription) => prescription.id === id
        );
        if (item) {
          this.startDrugSelectionFromSearch(item, true);
          break;
        }
      }
    },

    addFreeTextDrug() {
      this.selectedDrugFromSearch = {
        freeText: true,
      };
      this.editionMode = false;
      this.addFreeTextDrugAction({
        pages: this.prescriptionPages.length,
      });
      this.$nextTick(() => {
        this.drugsDialogModel = true;
      });
    },

    getItemIndexFromPageGroupedPrescriptions(id) {
      for (const page of this.prescriptionPages) {
        for (let prescriptionIndex in page) {
          if (page[prescriptionIndex].id === id) {
            return parseInt(prescriptionIndex, 10);
          }
        }
      }
      return null;
    },

    getDrugTextDisplay(id, name, option) {
      let text = `${
        this.getItemIndexFromPageGroupedPrescriptions(id) + 1
      }) ${name}`;
      if (option) {
        text += ` - ${option.concentration}`;
      }
      return text;
    },

    async filterDrugsFromSearch(args) {
      this.filteredListOfDrugs = await this.filterDrugsFromSearchAction({
        selectedDrugFromSearch: this.selectedDrugFromSearch,
        ...args,
      });
      this.searchLoadingTrigger = this._getTimestamp();
    },

    async startDrugSelectionFromSearch(item, editionMode = false) {
      this.editionMode = editionMode;
      if (!item.freeText) {
        const { doses, options } = await this.getItemDosesAndOptionsAction({
          item,
        });
        item["options"] = options;
        item["doses"] = doses;
      }
      this.searchTriggerModel = false;
      const { dose } = this.selectedDrugFromSearch || {};
      this.selectedDrugFromSearch = {
        ...item,
        dose: dose || item.dose,
      };

      if (item.option || item.freeText) {
        this.continueDrugSelectionAfterDisplayChoice(item, editionMode);
      } else {
        this.clearNodesModelsAndInputs();
        this.drugsDialogModel = true;
      }
    },

    continueDrugSelectionAfterDisplayChoice(item, editionMode = false) {
      this.continueDrugSelectionAfterDisplayChoiceAction({
        item,
        editionMode,
        pages: this.prescriptionPages.length,
      });
      this.$nextTick(() => {
        this.drugsDialogModel = true;
      });
    },

    setSelectedDrugFromSearchWithModels(models) {
      if (this.selectedDrugFromSearch) {
        this.selectedDrugFromSearch = {
          ...this.selectedDrugFromSearch,
          ...models,
        };
      }
    },

    addDrugToPrescriptionList() {
      let { freeTextNameDisplay, page } = this.selectedDrugFromSearch;
      if (page === undefined) {
        this.selectedDrugFromSearch.page = 0;
        page = 0;
      }
      if (freeTextNameDisplay) {
        this.selectedDrugFromSearch.name =
          this.selectedDrugFromSearch.freeTextNameDisplay;
        this.selectedDrugFromSearch.id = this._getTimestamp() + "";
        this.selectedDrugFromSearch.freeText = true;
      }
      if (!this.prescriptionPages[page]) {
        this.prescriptionPages[page] = [];
      }
      this.prescriptionPages[page].push(this.selectedDrugFromSearch);
      this.checkAndAdjustEmptyTabs(this.selectedDrugFromSearch.id);
    },

    saveDrugEditionToPrescriptionList() {
      this.editionMode = false;
      for (let pageIndex in this.prescriptionPages) {
        const page = this.prescriptionPages[pageIndex];
        for (let prescriptionIndex in page) {
          const item = page[prescriptionIndex];
          if (
            item.id === this.selectedDrugFromSearch.id &&
            item.page === this.selectedDrugFromSearch.page
          ) {
            this.prescriptionPages[pageIndex][prescriptionIndex] =
              this.selectedDrugFromSearch;
            this.checkAndAdjustEmptyTabs(this.selectedDrugFromSearch.id);
            return;
          } else if (item.id === this.selectedDrugFromSearch.id) {
            this.prescriptionPages[pageIndex].splice(prescriptionIndex, 1);
            this.addDrugToPrescriptionList();
            return;
          }
        }
      }
    },

    checkAndAdjustEmptyTabs(targetItemId) {
      this.$nextTick(() => {
        const updatedGroupedPrescriptions = [];
        let pageModel = 0;
        for (const pageKey in this.prescriptionPages) {
          if (!this.prescriptionPages[pageKey].length) {
            continue;
          }
          updatedGroupedPrescriptions.push(
            this.prescriptionPages[pageKey].map((item) => {
              item.page = updatedGroupedPrescriptions.length;
              if (item.id === targetItemId) {
                pageModel = updatedGroupedPrescriptions.length;
              }
              return item;
            })
          );
        }
        this.prescriptionPages = [...updatedGroupedPrescriptions];
        if (targetItemId) {
          this.pageModel = pageModel;
        }

        this._setupScreenSizes(this);
        this.$nextTick(() => {
          this.updateDraftFromPrescriptionAction();
        });
      });
    },
  },

  created() {},

  async mounted() {
    this._setupScreenSizes(this);
    this.hasCompleteRegistration = await this.checkForMedicalData();
    if (this.selectedDrugFromSearch) {
      this.startDrugSelectionFromSearch(
        this.selectedDrugFromSearch,
        this.editionMode
      );
    } else if (this.shareDate) {
      this.clearPrescriptionAction();
    }
    this.dialogHeight = this._getDialogHeight(this._isIos() ? 0.9 : 0.95);
  },
};
</script>
